import { useCallback, useEffect, useImperativeHandle, useMemo, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { Resources } from '@shared/localizations/Resources';
import { RuleType, RulesSet } from '@shared/utils/Rules/RulesSet';
import { PositionsStorage } from '@shared/utils/Storages/Table/PositionsStorage';
import { PositionsTableTotalService } from '@shared/utils/Table/Positions/PositionsTableTotalService';
import { createPositionTableColumns } from '@shared/utils/Table/Positions/PositionTableColumns';
import { RowType } from '@shared/utils/Table/TableRow.interface';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { createToolbarActions } from './Helpers/CreateToolbarActions';
import { processTableButtonAction, processTableModifySLTPAction } from './Helpers/PositionsPanelActionsProcessor';
import { createContextMenuItems } from './Helpers/PositionsPanelContextMenuItems';
export function usePositionsPanel(props) {
    // #region State
    var _a;
    const { ref, linkColor, linkingProvider, positionsProvider, appSettings, tradingProvider, applicationScreensProvider, settings: panelSettings, onHeaderChanged } = props;
    const storage = useMemo(() => new PositionsStorage(positionsProvider, appSettings), [positionsProvider, appSettings]);
    const totalService = useMemo(() => new PositionsTableTotalService(storage), [storage]);
    const columns = useMemo(() => {
        var _a, _b, _c;
        return createPositionTableColumns({
            isProtectiveOrders: (_a = tradingProvider === null || tradingProvider === void 0 ? void 0 : tradingProvider.isAllowRule(RulesSet.PROTECTIVE_ORDERS, RuleType.FeatureFlag)) !== null && _a !== void 0 ? _a : false,
            isAllowSlTp: (_b = tradingProvider === null || tradingProvider === void 0 ? void 0 : tradingProvider.isAllowRule(RulesSet.FUNCTION_SLTP, RuleType.User)) !== null && _b !== void 0 ? _b : false,
            isShowProductType: (_c = tradingProvider === null || tradingProvider === void 0 ? void 0 : tradingProvider.isShowProductType()) !== null && _c !== void 0 ? _c : false
        });
    }, []);
    const snapshot = useSyncExternalStore(storage.subscribe, storage.getSnapshot);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const [panelName, setPanelName] = useState(Resources.getResource('panel.positions'));
    const [allPositions, setAllPositions] = useState([]);
    const [selectedPositions, setSelectedPositions] = useState([]);
    const [isShowToolbar, setIsShowToolbar] = useState(panelSettings.isShowToolbar);
    const [toolbarActions, setToolbarActions] = useState([]);
    const [isDisplayTrades, setIsDisplayTrades] = useState(panelSettings.isDisplayTrades);
    const [tableQuantityTotalMode, setTableQuantityTotalMode] = useState(panelSettings.tableQuantityTotalMode);
    const [isShowTotals, setIsShowTotals] = useState(panelSettings.tableSettings.isShowTotals);
    const [groupColumn, setGroupColumn] = useState((_a = columns.find((column) => column.id === panelSettings.tableSettings.groupColumnId && column.isAllowGrouping)) !== null && _a !== void 0 ? _a : null);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [contextMenuItems, setContextMenuItems] = useState([]);
    const [isAllowTrading, setIsAllowTrading] = useState(tradingProvider.isAllowTrading().isAllowed);
    const tableRef = useRef(null);
    // #endregion State
    // #region Events
    const updateToolbarActions = useCallback(() => {
        setToolbarActions(createToolbarActions(allPositions, selectedPositions, appSettings, tradingProvider, applicationScreensProvider));
    }, [allPositions, selectedPositions, appSettings, tradingProvider, applicationScreensProvider]);
    const onLocaleChange = useCallback(() => {
        setPanelName(Resources.getResource('panel.positions'));
        updateToolbarActions();
        forceUpdate();
    }, [forceUpdate, updateToolbarActions]);
    const onLockTradingChanged = useCallback(() => {
        updateToolbarActions();
        setIsAllowTrading(tradingProvider.isAllowTrading().isAllowed);
    }, [updateToolbarActions, tradingProvider]);
    const onSelectedRowsChanged = useCallback((selectedRows) => {
        const positions = [];
        for (const row of selectedRows) {
            if (row.type === RowType.GroupRow) {
                continue;
            }
            const position = storage.getPositionOrCorporateAction(row);
            if (position === null) {
                continue;
            }
            positions.push(position);
        }
        setSelectedPositions(positions);
        if (isValidArray(positions) && linkColor !== null) {
            linkingProvider.linkOutInstrument(linkColor, positions[0].getInstrument());
        }
    }, [storage, linkColor, linkingProvider]);
    const onTableButtonActionClick = useCallback((row, action) => {
        const position = storage.getPositionOrCorporateAction(row);
        if (position === null) {
            return;
        }
        processTableButtonAction(action, allPositions, [position], appSettings, tradingProvider, applicationScreensProvider, PlacedFrom.WEB_POSITIONS_PANEL_DB_CLICK);
    }, []);
    const onModifySLTPActionClick = useCallback((row, isSL) => {
        const position = storage.getPositionOrCorporateAction(row);
        if (position === null) {
            return;
        }
        processTableModifySLTPAction(position, isSL, tradingProvider, applicationScreensProvider, PlacedFrom.WEB_POSITIONS_PANEL_DB_CLICK);
    }, []);
    const updateContextMenuItems = () => {
        const items = createContextMenuItems({
            appSettings,
            tradingProvider,
            applicationScreensProvider,
            allPositions,
            selectedPositions,
            columns,
            isShowToolbar,
            isDisplayTrades,
            tableQuantityTotalMode,
            isShowTotals,
            groupByColumn: groupColumn,
            onShowToolbarClick: () => setIsShowToolbar(!isShowToolbar),
            onDisplayTradesClick: () => setIsDisplayTrades(!isDisplayTrades),
            onTableQuantityTotalModeClick: (mode) => setTableQuantityTotalMode(mode),
            onShowTotalsClick: () => setIsShowTotals(!isShowTotals),
            onGroupByClick: (groupByColumn) => setGroupColumn(groupByColumn)
        });
        setContextMenuItems(items);
    };
    const onContextMenuOpen = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
        updateContextMenuItems();
    };
    const onContextMenuClose = useCallback(() => {
        setContextMenuItems([]);
    }, []);
    // #endregion Events
    // #region Effects
    useEffect(() => {
        Resources.subscribeOnLocaleChange(onLocaleChange);
        return () => {
            Resources.unsubscribeOnLocaleChange(onLocaleChange);
        };
    }, []);
    useEffect(() => {
        const positions = [];
        for (const row of snapshot.rows) {
            const position = storage.getPositionOrCorporateAction(row);
            if (position === null) {
                continue;
            }
            allPositions.push(position);
        }
        setAllPositions(positions);
    }, [storage, snapshot]);
    useEffect(() => {
        onLockTradingChanged();
    }, [onLockTradingChanged]);
    useEffect(() => {
        tradingProvider.subscribeOnLockTrading(onLockTradingChanged);
        return () => {
            tradingProvider.unsubscribeOnLockTrading(onLockTradingChanged);
        };
    }, [tradingProvider, onLockTradingChanged]);
    useEffect(() => {
        var _a, _b;
        storage.isDisplayTrades = isDisplayTrades;
        totalService.tableQuantityTotalMode = tableQuantityTotalMode;
        (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.setIsShowTotals(isShowTotals);
        (_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.setGroupColumn(groupColumn);
        if (isValidArray(contextMenuItems)) {
            updateContextMenuItems();
        }
    }, [isShowToolbar, isDisplayTrades, tableQuantityTotalMode, isShowTotals, groupColumn]);
    useEffect(() => {
        const headerText = snapshot.rows.length > 0 ? `${panelName} (${snapshot.rows.length})` : panelName;
        if (onHeaderChanged) {
            onHeaderChanged(headerText);
        }
    }, [panelName, snapshot.rows.length, onHeaderChanged]);
    // #endregion Effects
    useImperativeHandle(ref, () => ({
        getSettings: () => {
            var _a, _b;
            return {
                isShowToolbar,
                isDisplayTrades,
                tableQuantityTotalMode,
                tableSettings: (_b = (_a = tableRef === null || tableRef === void 0 ? void 0 : tableRef.current) === null || _a === void 0 ? void 0 : _a.getSettings()) !== null && _b !== void 0 ? _b : {}
            };
        }
    }));
    return {
        tableRef,
        panelName,
        isShowToolbar,
        toolbarActions,
        columns,
        rows: snapshot.rows,
        totalService,
        contextMenuPosition,
        contextMenuItems,
        isAllowTrading,
        subscribeRowUpdate: storage.subscribeRowUpdate,
        subscribeTableUpdate: storage.subscribeTableUpdate,
        onSelectedRowsChanged,
        onTableButtonActionClick,
        onModifySLTPActionClick,
        onContextMenuOpen,
        onContextMenuClose
    };
}
